import { bisector } from 'd3-array'
import { format as d3format } from 'd3-format'

import { throttle } from 'lodash'

import CanvasChart from '../common/CanvasChart'
import Tooltip from '../common/Tooltip'
import Legend from '../common/Legend'
import LithologyPatterns from './LithologyPatterns'
import TrackContext from '../render/TrackContext'
import { DashTheme } from '../wbs/types'
import { ScaleLinear } from 'd3'
import { Start } from '../../types'

type State = {
    start: Start
    items: {
        lithologys: Lithology[]
        top: number
        bottom: number
    }[]
}

export type LithologyPc = {
    text: string
    value: string
}

type Lithology = {
    code: number
    color: string
    description: string
    lithPc: LithologyPc
}

export default class LithologyChart extends CanvasChart<State> {
    protected legend_: Legend
    protected patterns: LithologyPatterns
    protected legendRenderHoveredValues: (x: LithologyPc[], y: string) => void

    init(): void {
        super.init()
        this.patterns = new LithologyPatterns()
        this.el.classed('lithology-chart', true)
        this.tooltip = new Tooltip<State>()
        this.legend_ = new Legend(this._legend.node(), this.theme, false)
        this.legendRenderHoveredValues = throttle(
            (x: LithologyPc[], y: string) => this.legend_.renderHoveredValues(x, y),
            100
        )
    }

    measureText(text: string): number {
        const canvas = document.createElement('canvas')
        const context = canvas.getContext('2d')
        const fontFace = window.getComputedStyle(document.documentElement).getPropertyValue('--font-regular')

        context.font = `${12}px ${fontFace}`

        return context.measureText(text).width
    }

    renderTooltip(tooltipPosition: number, tooltipText: string): void {
        if (!tooltipPosition) {
            this.el.selectAll('div.lithology-tooltip').style('visibility', 'hidden')

            return
        }

        const formattedTooltipText = `Depth: ${tooltipText}`
        const measuredText = this.measureText(formattedTooltipText)

        const tooltipLayer = this.el
            .selectAll('div.lithology-tooltip')
            .data([tooltipPosition])
            .join('div')
            .attr('class', 'lithology-tooltip')
            .style('visibility', 'visible')
            .style('position', 'absolute')
            .style('z-index', '1')
            .style(
                'top',
                this.isVertical
                    ? tooltipPosition <= this.innerHeight * 0.7
                        ? `${this.legendSize + tooltipPosition + 20}px`
                        : `${tooltipPosition + this.legendSize - 40}px`
                    : `${this.margin.top}px`
            )
            .style(
                'left',
                this.isVertical
                    ? `${this.margin.left}px`
                    : tooltipPosition <= this.innerWidth * 0.7
                        ? `${this.legendSize + tooltipPosition + 20}px`
                        : measuredText + this.legendSize >= this.innerWidth
                            ? `${this.legendSize + 20}px`
                            : `${tooltipPosition - 20 - this.legendSize}px`
            )
            .style('height', 'max-content')

        const tooltipContainer = tooltipLayer
            .selectAll('div.lithology-container')
            .data([tooltipPosition])
            .join('div')
            .attr('class', 'lithology-container')
            .style('background-color', 'var(--gray-100)')
            .style('border', '1px solid var(--gray-500)')
            .style('word-wrap', 'break-word')
            .style('display', 'flex')
            .style('padding', '5px')

        tooltipContainer
            .selectAll('p.tooltip-item')
            .data([tooltipPosition])
            .join('p')
            .attr('class', 'tooltip-item')
            .style('font-weight', 'bold')
            .style('margin', '0')
            .style('line-height', '20px')
            .attr('color', 'var(--gray-800)')
            .text(formattedTooltipText)
    }

    onMouseMove(mouse: [number, number], scale: ScaleLinear<number, number>): void {
        const state = this.subject.getValue()
        // TODO: move to track chart
        if (!state.start) {
            return
        }
        const depthUom = state.start.info.depth_uom
        const x = this.isVertical ? mouse[1] : mouse[0]
        const y = this.isVertical ? mouse[1] : mouse[0]
        const bisect = bisector(d => d.top).left

        const format = d3format('.02f')

        if (state.items) {
            const position = scale.invert(x)

            const found = bisect(state.items, position)

            if (!found) {
                this.renderTooltip(null, '')
            }

            if (found) {
                const contents = this._contents.node()
                if (contents) {
                    const item = state.items[found - 1]

                    if (position > item.bottom) {
                        this.renderTooltip(null, '')
                    }

                    if (position < item.bottom) {
                        const offset = contents.getBoundingClientRect()

                        // update box offset.
                        this.tooltip.setOffset({
                            left: offset.left + (this.isVertical ? this.axisSize : 0),
                            top: offset.top
                        })
                        if (item.lithologys.length > 0) {
                            const lithPcValue: LithologyPc[] = item.lithologys.map((lithology: Lithology) => {
                                return {
                                    text: lithology.description,
                                    value: `${lithology.lithPc.value}`
                                }
                            })
                            const depth = `${format(item.top)}${depthUom} to ${format(item.bottom)}${depthUom}`
                            this.legendRenderHoveredValues(lithPcValue, depth.concat(' (hover area)'))
                            this.renderTooltip(x, depth.concat(' (hover area)'))

                            return
                        }
                    }
                }
            }
        }
        this.legend_.clearHover()
    }

    /**
     * Render legend
     * @param data
     */
    renderLegend(): void {
        this._legend
            .style('height', this.isVertical ? `${this.legendSize}px` : 'auto')
            .style('width', this.isVertical ? 'auto' : `${this.legendSize}px`)

        if (!this.patterns.isEmpty()) {
            this.legend_.render(
                [...this.patterns.values()].map(e => {
                    return {
                        text: e.description,
                        color: e.color,
                        background: e.img ? e.img.src : ''
                    }
                }),
                this.isVertical ?? false,
                this.legendSize,
                this.theme,
                false
            )
        }
    }

    /**
     * Load image patterns
     * @param data
     */
    loadPatterns(data: State): void {
        if (data.items?.length > 0) {
            this.patterns.load(data.items, () => {
                this.render(this.subject.getValue())
            })
        }
    }

    /**
     * Build lithology canvas
     * @param canvas
     * @param data
     * @param scale
     */
    renderCanvas(canvas: HTMLCanvasElement, data: State, scale: ScaleLinear<number, number>): void {
        if (!canvas) {
            return
        }

        if (data.items?.length > 0) {
            this.patterns.load(data.items, () => {
                this.render(this.subject.getValue())
            })

            const trackCtx = new TrackContext(canvas, scale, this.isVertical)
            if (trackCtx) {
                trackCtx.clear()
                trackCtx.globalCompositeOperation = 'destination-over'

                data.items.forEach(item => {
                    let lithPcTotalAcc = 0

                    const lithPcTotal = item.lithologys.reduce((state: number, obj: { lithPc: { value: number } }) => {
                        return state + obj.lithPc.value
                    }, 0)

                    const fillX = item.top
                    const fillWidth = item.bottom - fillX

                    for (let x = 0; x < item.lithologys.length; x++) {
                        const lithology = item.lithologys[x]

                        const fillY = lithPcTotalAcc
                        lithPcTotalAcc += lithology.lithPc.value / lithPcTotal
                        const fillHeight = lithPcTotalAcc

                        // fill pattern
                        const fillStyle = this.patterns.getCanvasPattern(lithology.code, trackCtx.getCtx())
                        if (fillStyle && fillWidth > 1) {
                            trackCtx.fillStyle = fillStyle
                            trackCtx.fillRect(fillX, fillY, fillWidth, fillHeight)
                        }
                        // fill color
                        trackCtx.fillStyle = lithology.color
                        trackCtx.fillRect(fillX, fillY, fillWidth, fillHeight)
                    }
                })
            }
        }
    }

    get theme(): DashTheme {
        return this.options.dashboardContext.theme
    }

    destroy(): void {
        super.destroy()
        this.legend_.destroy()
    }

}
